import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  CheckCircle as CheckCircleIcon,
  ChevronRight as ChevronRightIcon,
  EventAvailable as EventAvailableIcon,
  LockOutlined as LockOutlinedIcon,
  ScheduleOutlined as ScheduleOutlinedIcon,
} from '@mui/icons-material';
import {
  BookingAvailableDay,
  BookingAvailability,
  BookingServiceOption,
  PublicWebsiteData,
  portalApi,
} from '../services/portalApi';
import {
  createBookingResizeMessage,
  isBookingEmbedMode,
} from '../utils/bookingEmbed';
import BookingAddressAutocomplete from '../components/BookingAddressAutocomplete';
import { useTranslation } from 'react-i18next';
import { applyLanguage, i18n } from '../i18n';

type Step = 'service' | 'date' | 'details' | 'success';

const BOOKING_STEPS: Array<{ id: Exclude<Step, 'success'>; label: string }> = [
  { id: 'service', label: 'Service' },
  { id: 'date', label: 'Date' },
  { id: 'details', label: 'Details' },
];

const parseLocalDate = (date: string) => {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (date: string, locale?: string) =>
  parseLocalDate(date).toLocaleDateString(locale, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

const formatDateCard = (date: string, locale?: string) => ({
  weekday: parseLocalDate(date).toLocaleDateString(locale, { weekday: 'short' }),
  month: parseLocalDate(date).toLocaleDateString(locale, { month: 'short' }),
  day: parseLocalDate(date).toLocaleDateString(locale, { day: 'numeric' }),
});

const formatPrice = (
  service: BookingServiceOption,
  availability?: BookingAvailability | null,
) => {
  if (service.price === null) return null;
  if (service.price === 0) return i18n.t('Free');

  const price = new Intl.NumberFormat(availability?.locale || 'en-US', {
    style: 'currency',
    currency: availability?.currencyCode || 'USD',
    maximumFractionDigits: 0,
  }).format(service.price);
  const priceType = service.priceType?.toLowerCase().replace(/[_-]/g, ' ') || '';
  return priceType.includes('start') ? i18n.t('From {{price}}', { price }) : price;
};

const BookingProgress: React.FC<{
  step: Step;
  primaryColor: string;
}> = ({ step, primaryColor }) => {
  const { t } = useTranslation();
  if (step === 'success') return null;
  const currentIndex = BOOKING_STEPS.findIndex((item) => item.id === step);

  return (
    <Box aria-label={t('Booking progress')} sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
      {BOOKING_STEPS.map((item, index) => {
        const isComplete = index < currentIndex;
        const isCurrent = index === currentIndex;
        return (
          <React.Fragment key={item.id}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: { xs: 58, sm: 76 } }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: isComplete || isCurrent ? primaryColor : 'transparent',
                  color: isComplete || isCurrent ? '#fff' : 'text.secondary',
                  border: '1.5px solid',
                  borderColor: isComplete || isCurrent ? primaryColor : 'divider',
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {isComplete ? <CheckIcon sx={{ fontSize: 17 }} /> : index + 1}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  mt: 0.75,
                  color: isCurrent ? 'text.primary' : 'text.secondary',
                  fontWeight: isCurrent ? 700 : 500,
                }}
              >
                {t(item.label)}
              </Typography>
            </Box>
            {index < BOOKING_STEPS.length - 1 && (
              <Box
                sx={{
                  flex: 1,
                  height: 2,
                  mt: '14px',
                  bgcolor: index < currentIndex ? primaryColor : 'divider',
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export const PublicBooking: React.FC = () => {
  const { t } = useTranslation();
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const slug = useMemo(() => {
    if (routeSlug) return routeSlug;
    const hostname = window.location.hostname;
    if (hostname.endsWith('.pooldial.com')) {
      const subdomain = hostname.split('.')[0];
      if (subdomain && !['app', 'portal', 'www'].includes(subdomain)) {
        return subdomain;
      }
    }
    return '';
  }, [routeSlug]);
  const isEmbedded = useMemo(
    () => isBookingEmbedMode(window.location.search),
    [],
  );
  const [website, setWebsite] = useState<PublicWebsiteData | null>(null);
  const [availability, setAvailability] = useState<BookingAvailability | null>(null);
  const [selectedService, setSelectedService] = useState<BookingServiceOption | null>(null);
  const [selectedDay, setSelectedDay] = useState<BookingAvailableDay | null>(null);
  const [step, setStep] = useState<Step>('service');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    customerAddress: '',
    notes: '',
    website: '',
  });

  useEffect(() => {
    const loadBooking = async () => {
      if (!slug) {
        setError(t('Online booking is not available from this portal address.'));
        setLoading(false);
        return;
      }

      try {
        const [websiteResult, availabilityData] = await Promise.all([
          portalApi.getPublicWebsite(slug).catch(() => null),
          portalApi.getBookingAvailability(slug),
        ]);
        applyLanguage(
          availabilityData.customerLanguage || availabilityData.locale,
        );
        setWebsite(websiteResult);
        setAvailability(availabilityData);
      } catch (err: any) {
        setError(err.message || t('Online booking is not available right now.'));
      } finally {
        setLoading(false);
      }
    };

    loadBooking();
  }, [slug, t]);

  useEffect(() => {
    if (!isEmbedded || !slug || window.parent === window) return undefined;

    let animationFrame = 0;
    const sendHeight = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const height = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
        );
        window.parent.postMessage(createBookingResizeMessage(slug, height), '*');
      });
    };

    const observer = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(sendHeight);
    observer?.observe(document.body);
    window.addEventListener('load', sendHeight);
    sendHeight();

    return () => {
      window.cancelAnimationFrame(animationFrame);
      observer?.disconnect();
      window.removeEventListener('load', sendHeight);
    };
  }, [error, isEmbedded, loading, slug, step]);

  const services = useMemo(() => {
    const allServices = availability?.services ?? [];
    const configuredIds = availability?.bookingServiceTypes
      ?? website?.settings?.bookingServiceTypes
      ?? [];
    if (configuredIds.length === 0) return allServices;

    const configuredServices = allServices.filter((service) => configuredIds.includes(service.id));
    return configuredServices.length > 0 ? configuredServices : allServices;
  }, [availability?.bookingServiceTypes, availability?.services, website?.settings?.bookingServiceTypes]);

  const primaryColor = availability?.primaryColor
    || website?.settings?.primaryColor
    || '#016ec3';
  const businessName = availability?.businessName
    || website?.account.businessName
    || t('Your pool service company');
  const logoUrl = availability?.logoUrl || website?.settings.logoUrl;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedDay || !selectedService || form.website) return;
    if (!form.customerName.trim() || !form.customerPhone.trim()) {
      setError(t('Enter your name and phone number so the appointment can be confirmed.'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await portalApi.createPublicBooking(slug, {
        routeId: selectedDay.routeId,
        date: selectedDay.date,
        serviceType: 'one_time',
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone.trim(),
        customerEmail: form.customerEmail.trim() || undefined,
        customerAddress: form.customerAddress.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      setStep('success');
    } catch (err: any) {
      setError(err.message || t('Unable to send your request. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: isEmbedded ? 420 : '100vh',
          display: 'grid',
          placeItems: 'center',
          bgcolor: isEmbedded ? 'transparent' : 'background.default',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={36} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('Finding available appointments…')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="public-booking-page"
      data-booking-embed={isEmbedded ? 'true' : undefined}
      sx={{
        minHeight: isEmbedded ? 'auto' : '100vh',
        bgcolor: isEmbedded ? 'transparent' : 'background.default',
        pb: isEmbedded ? 0 : { xs: 'calc(96px + env(safe-area-inset-bottom))', sm: 6 },
      }}
    >
      {!isEmbedded && (
        <Box
          component="header"
          sx={{
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 16px 30px rgba(44, 42, 41, 0.04)',
          }}
        >
          <Container
            maxWidth="lg"
            sx={{
              minHeight: 72,
              py: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {logoUrl && (
              <Box
                component="img"
                src={logoUrl}
                alt={businessName}
                sx={{ height: 52, maxWidth: { xs: 190, sm: 260 }, objectFit: 'contain' }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap>{businessName}</Typography>
              <Typography variant="body2" color="text.secondary">{t('Customer portal')}</Typography>
            </Box>
          </Container>
        </Box>
      )}

      <Container
        maxWidth="lg"
        disableGutters={isEmbedded}
        sx={{ py: isEmbedded ? 0 : { xs: 2.5, sm: 3 }, px: isEmbedded ? 0 : { xs: 2, sm: 3 } }}
      >
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Card
          sx={{
            overflow: 'hidden',
            borderRadius: '10px',
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 3 },
              '&:last-child': { pb: { xs: 2, sm: 3 } },
            }}
          >
            {isEmbedded && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2.5, mb: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                {logoUrl && <Box component="img" src={logoUrl} alt={businessName} sx={{ height: 44, maxWidth: 200, objectFit: 'contain' }} />}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>{businessName}</Typography>
                  <Typography variant="caption" color="text.secondary">{t('Online appointment request')}</Typography>
                </Box>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <LockOutlinedIcon sx={{ color: primaryColor, fontSize: 15 }} /> {t('No payment required')}
              </Typography>
            </Box>

            <BookingProgress step={step} primaryColor={primaryColor} />
            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }}>{error}</Alert>}

            <Box>
              {step === 'service' && (
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" gutterBottom>
                      {availability?.bookingHeadline
                        || website?.settings.bookingHeadline
                        || t('What can we help with?')}
                    </Typography>
                    <Typography color="text.secondary">
                      {availability?.bookingDescription
                        || website?.settings.bookingDescription
                        || t('Choose a service to see the next available dates.')}
                    </Typography>
                  </Box>

                  {services.length === 0 ? (
                    <Alert severity="info">{t('Online booking is not available right now. Please contact {{businessName}} directly.', { businessName })}</Alert>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {services.map((service) => {
                        const details = [
                          availability?.showDuration !== false && service.duration ? `${service.duration} min` : null,
                          availability?.showPrice !== false ? formatPrice(service, availability) : null,
                        ].filter((value): value is string => Boolean(value));

                        return (
                          <Button
                            key={service.id}
                            onClick={() => {
                              setError(null);
                              setSelectedService(service);
                              setSelectedDay(null);
                              setStep('date');
                            }}
                            variant="outlined"
                            endIcon={<ChevronRightIcon />}
                            sx={{
                              justifyContent: 'space-between',
                              textAlign: 'left',
                              borderRadius: '10px',
                              borderColor: 'divider',
                              color: 'text.primary',
                              px: { xs: 2, sm: 2.5 },
                              py: 1.75,
                              minHeight: 76,
                              '& .MuiButton-endIcon': { color: primaryColor },
                              '&:hover': {
                                borderColor: primaryColor,
                                bgcolor: alpha(primaryColor, 0.04),
                              },
                            }}
                          >
                            <Box sx={{ pr: 2 }}>
                              <Typography fontWeight={700} sx={{ lineHeight: 1.25 }}>{service.name}</Typography>
                              {service.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                                  {service.description}
                                </Typography>
                              )}
                              {details.length > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                  {details.join(' · ')}
                                </Typography>
                              )}
                            </Box>
                          </Button>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}

              {step === 'date' && selectedService && (
                <Box>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => { setError(null); setStep('service'); }}
                    sx={{ mb: 2, px: 0.5, color: 'text.secondary', '&:hover': { bgcolor: 'transparent', color: primaryColor } }}
                  >
                    {t('Back to services')}
                  </Button>
                  <Typography variant="h4" gutterBottom>
                    {t('Choose a preferred date')}
                  </Typography>
                  <Typography color="text.secondary">
                    {t('Pick a day that works for you. {{businessName}} will confirm the exact arrival time.', { businessName })}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      mt: 2.5,
                      p: 1.5,
                      borderRadius: '10px',
                      bgcolor: alpha(primaryColor, 0.06),
                    }}
                  >
                    <EventAvailableIcon sx={{ color: primaryColor }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('Selected service')}</Typography>
                      <Typography variant="body2" fontWeight={700}>{selectedService.name}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 1.25, mt: 2.5 }}>
                    {(availability?.availableDays ?? []).map((day) => {
                      const date = formatDateCard(day.date, availability?.locale);
                      return (
                        <Button
                          key={`${day.date}-${day.routeId}`}
                          variant="outlined"
                          aria-label={t('Choose {{date}}', { date: formatDate(day.date, availability?.locale) })}
                          onClick={() => {
                            setError(null);
                            setSelectedDay(day);
                            setStep('details');
                          }}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            borderRadius: '10px',
                            p: 1.5,
                            minHeight: 94,
                            borderColor: 'divider',
                            color: 'text.primary',
                            '&:hover': { borderColor: primaryColor, bgcolor: alpha(primaryColor, 0.04) },
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                            {date.weekday}
                          </Typography>
                          <Typography sx={{ fontSize: 25, fontWeight: 700, lineHeight: 1.1, mt: 0.25 }}>{date.day}</Typography>
                          <Typography variant="body2" color="text.secondary">{date.month}</Typography>
                        </Button>
                      );
                    })}
                  </Box>
                  {availability?.availableDays.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2.5 }}>{t('No dates are available right now. Please check back later.')}</Alert>
                  )}
                </Box>
              )}

              {step === 'details' && selectedService && selectedDay && (
                <Box component="form" onSubmit={handleSubmit}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => { setError(null); setStep('date'); }}
                    sx={{ mb: 2, px: 0.5, color: 'text.secondary', '&:hover': { bgcolor: 'transparent', color: primaryColor } }}
                  >
                    {t('Back to dates')}
                  </Button>
                  <Typography variant="h4" gutterBottom>
                    {t('Where should we confirm your visit?')}
                  </Typography>
                  <Typography color="text.secondary">
                    {t('Your phone number is required so {{businessName}} can confirm the appointment.', { businessName })}
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 230px' },
                      gap: 3,
                      mt: 3,
                    }}
                  >
                    <Box>
                      <TextField sx={{ display: 'none' }} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.75 }}>
                        <TextField label={t('Full name')} required autoComplete="name" value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} />
                        <TextField label={t('Phone')} required type="tel" autoComplete="tel" value={form.customerPhone} onChange={(e) => setForm({ ...form, customerPhone: e.target.value })} />
                        <TextField label={t('Email (optional)')} type="email" autoComplete="email" value={form.customerEmail} onChange={(e) => setForm({ ...form, customerEmail: e.target.value })} />
                        <BookingAddressAutocomplete
                          slug={slug}
                          value={form.customerAddress}
                          onChange={(customerAddress) =>
                            setForm((current) => ({ ...current, customerAddress }))
                          }
                        />
                      </Box>
                      <TextField
                        label={t('Anything else we should know? (optional)')}
                        multiline
                        minRows={3}
                        fullWidth
                        sx={{ mt: 1.75 }}
                        value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      />
                    </Box>

                    <Box
                      component="aside"
                      aria-label={t('Appointment summary')}
                      sx={{
                        alignSelf: 'start',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '10px',
                        p: 2,
                        bgcolor: alpha(primaryColor, 0.035),
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>{t('Request summary')}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                        <EventAvailableIcon sx={{ color: primaryColor, fontSize: 20, mt: 0.2 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">{t('Service')}</Typography>
                          <Typography variant="body2" fontWeight={700}>{selectedService.name}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <ScheduleOutlinedIcon sx={{ color: primaryColor, fontSize: 20, mt: 0.2 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">{t('Preferred date')}</Typography>
                          <Typography variant="body2" fontWeight={700}>{formatDate(selectedDay.date, availability?.locale)}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2, mt: 3 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      <LockOutlinedIcon sx={{ fontSize: 15 }} /> {t('No payment is collected. Your information is sent securely.')}
                    </Typography>
                    <Button
                      type="submit"
                      disabled={submitting}
                      variant="contained"
                      size="large"
                      sx={{
                        bgcolor: primaryColor,
                        px: 3,
                        minWidth: 210,
                        '&:hover': { bgcolor: primaryColor, filter: 'brightness(0.92)' },
                      }}
                    >
                      {submitting ? t('Sending request…') : t('Send appointment request')}
                    </Button>
                  </Box>
                </Box>
              )}

              {step === 'success' && selectedService && selectedDay && (
                <Box sx={{ textAlign: 'center', maxWidth: 540, mx: 'auto', py: { xs: 4, sm: 7 } }}>
                  <Box
                    sx={{
                      width: 82,
                      height: 82,
                      display: 'grid',
                      placeItems: 'center',
                      borderRadius: '50%',
                      bgcolor: alpha(primaryColor, 0.1),
                      mx: 'auto',
                      mb: 2.5,
                    }}
                  >
                    <CheckCircleIcon sx={{ color: primaryColor, fontSize: 52 }} />
                  </Box>
                  <Typography variant="h4" gutterBottom>
                    {t('Your request is in')}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    {t('{{businessName}} will contact you to confirm the arrival time for your {{serviceName}} visit.', { businessName, serviceName: selectedService.name })}
                  </Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderRadius: '10px', bgcolor: alpha(primaryColor, 0.06) }}>
                    <EventAvailableIcon sx={{ color: primaryColor }} />
                    <Typography fontWeight={700}>{formatDate(selectedDay.date, availability?.locale)}</Typography>
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary">{t('Booking powered by Pool Dial')}</Typography>
            </Box>
          </CardContent>
        </Card>
        </Box>
      </Container>
    </Box>
  );
};
